/**
 * Admin self-report dialog — "Report a bug / request a feature" (intent
 * pipeline phase 3). Files to POST /admins/feedback, which creates a labeled
 * GitHub issue the weekly /intake consumes. Mounted once in
 * OrganizationLayout; opened from anywhere via the OPEN_FEEDBACK_EVENT window
 * event (palette action + avatar menu dispatch it), mirroring how the topbar
 * opens the ⌘K palette.
 */
import { useEffect, useState } from 'react';
import { Bug, Lightbulb } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

export const OPEN_FEEDBACK_EVENT = 'staty:report-feedback';

export function openFeedbackDialog() {
  window.dispatchEvent(new CustomEvent(OPEN_FEEDBACK_EVENT));
}

type FeedbackType = 'bug' | 'feature';

export default function FeedbackDialog() {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<FeedbackType>('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(OPEN_FEEDBACK_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_FEEDBACK_EVENT, onOpen);
  }, []);

  const canSubmit = title.trim().length >= 5 && description.trim().length > 0 && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.API_URL}/api/v1/admins/feedback`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, title: title.trim(), description: description.trim() }),
      });
      if (res.ok) {
        toast.success(type === 'bug' ? 'Bug report filed, thank you!' : 'Feature request filed, thank you!');
        setOpen(false);
        setTitle('');
        setDescription('');
        setType('bug');
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.error || "Couldn't file the report. Please try again.");
      }
    } catch {
      toast.error("Couldn't file the report. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Report a bug or request a feature</DialogTitle>
          <DialogDescription>
            Goes straight to the Staty team. The more specific, the faster we can act on it.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Report type">
            <Button
              type="button"
              variant={type === 'bug' ? 'default' : 'outline'}
              role="radio"
              aria-checked={type === 'bug'}
              onClick={() => setType('bug')}
            >
              <Bug className="mr-2 h-4 w-4" />
              Bug
            </Button>
            <Button
              type="button"
              variant={type === 'feature' ? 'default' : 'outline'}
              role="radio"
              aria-checked={type === 'feature'}
              onClick={() => setType('feature')}
            >
              <Lightbulb className="mr-2 h-4 w-4" />
              Feature idea
            </Button>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="feedback-title">Summary</Label>
            <Input
              id="feedback-title"
              value={title}
              maxLength={120}
              placeholder={type === 'bug' ? 'What went wrong?' : 'What should Staty do?'}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="feedback-description">Details</Label>
            <Textarea
              id="feedback-description"
              value={description}
              maxLength={5000}
              rows={5}
              placeholder={
                type === 'bug'
                  ? 'What did you do, what happened, and what did you expect? Which page?'
                  : 'What are you trying to accomplish, and where does Staty fall short today?'
              }
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button type="button" onClick={submit} disabled={!canSubmit}>
            {submitting ? 'Sending…' : 'Send report'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
